import React from 'react';
import {
  IconButton,
  Tooltip,
  CircularProgress,
  Box,
  Typography,
} from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import useAuth from 'src/hooks/useAuth';
import { useWatchList } from 'src/contexts/WatchListContext';
import { useSnackbar } from 'notistack';
import {
  trackWatchListAdd,
  trackWatchListRemove,
} from 'src/utils/analyticsTracking';
import { WATCH_LIST_SOURCES } from 'src/constants/analytics';

export interface WatchToggleProps {
  certificationId: number;
  size?: 'small' | 'medium' | 'large';
  showError?: boolean;
  showTooltip?: boolean;
  disabled?: boolean;
  className?: string;
  onToggle?: (isWatched: boolean) => void;
  source?: string; // New prop to track analytics context
}

const WatchToggle: React.FC<WatchToggleProps> = ({
  certificationId,
  size = 'small',
  showError = true,
  showTooltip = true,
  disabled = false,
  className,
  onToggle,
  source = WATCH_LIST_SOURCES.CERTIFICATION_PAGE, // Default to certification_page for backward compatibility
}) => {
  const { enqueueSnackbar } = useSnackbar();
  const { user } = useAuth();
  const {
    watchedCerts,
    watchedCertsCount,
    watchedCertsLimit,
    loading: watchLoading,
    error: watchError,
    handleRemoveWatch,
    handleAddToWatchList,
  } = useWatchList();

  // Get watched certification IDs from the context
  const watchedIds = watchedCerts.map((cert) => cert.id);

  // Watch/Unwatch logic
  const isWatched = watchedIds.includes(certificationId);
  const canShowWatch = user?.canUseRealTimeNotificationsWatched;

  const handleToggleWatch = async (): Promise<void> => {
    // Enforce certification limit
    if (!isWatched && watchedCertsCount >= watchedCertsLimit) {
      enqueueSnackbar(
        `You are watching the maximum number of certifications (${watchedCertsLimit}). Remove one before watching another.`,
        { variant: 'warning' }
      );
      return;
    }

    if (isWatched) {
      // Track removal from certification page
      trackWatchListRemove(source, [certificationId], {
        // Note: We don't have property_id, household_id, or household_name in this context
        // These would need to be passed as props if needed
      });

      await handleRemoveWatch([certificationId]);
    } else {
      // Track addition from certification page
      trackWatchListAdd(source, [certificationId], {
        // Note: We don't have property_id, household_id, or household_name in this context
        // These would need to be passed as props if needed
      });

      await handleAddToWatchList([certificationId]);
    }

    // Call the optional callback
    if (onToggle) {
      onToggle(!isWatched);
    }
  };

  const getEyeIcon = (): React.ReactElement => {
    if (isWatched) {
      return <VisibilityIcon sx={{ color: '#1976d2' }} />;
    }
    return <VisibilityOffIcon color="action" />;
  };

  const getEyeTooltip = (): string => {
    if (!isWatched && watchedCertsCount >= watchedCertsLimit) {
      return `You are watching the maximum number of certifications (${watchedCertsLimit}). Remove one before watching another.`;
    }
    if (isWatched) {
      return 'You are watching this certification. Click to stop watching.';
    }
    return 'You are not watching this certification. Click to start watching.';
  };

  // If user doesn't have watch permission, don't render anything
  if (!canShowWatch) {
    return null;
  }

  // Check if we're at the limit and trying to add a new watch
  const isAtLimit = !isWatched && watchedCertsCount >= watchedCertsLimit;
  const isDisabled = disabled || watchLoading || isAtLimit;

  const button = (
    <IconButton
      onClick={handleToggleWatch}
      disabled={isDisabled}
      color={isWatched ? 'primary' : 'default'}
      size={size}
      className={className}
    >
      {watchLoading ? (
        <CircularProgress size={size === 'small' ? 16 : 20} />
      ) : (
        getEyeIcon()
      )}
    </IconButton>
  );

  return (
    <Box sx={{ display: 'flex', alignItems: 'center' }}>
      {showTooltip ? (
        <Tooltip title={getEyeTooltip()}>
          <span>{button}</span>
        </Tooltip>
      ) : (
        button
      )}

      {/* Error display */}
      {showError && watchError && (
        <Typography
          variant="caption"
          sx={{ color: 'error.main', mt: 0.5, display: 'block' }}
        >
          {watchError}
        </Typography>
      )}
    </Box>
  );
};

export default WatchToggle;
